import dayjs from 'dayjs';
import { FC, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncSearch, Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import ControlledDataTable from 'components/tables/controlled-data-table';
import { DataTableRef } from 'components/tables/data-table';
import {
  BaseDataType,
  ControlledDataTableColumn,
  ControlledDataTableProvider,
  useDataTableContext
} from 'components/tables/data-table-context';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { useUnitApi } from 'hooks/use-unit-api';
import { transactionDataColumns } from 'pages/transactions';
import { downloadUngroupedCsv, fetchActiveUnitsUngrouped, getBookingByNumber } from 'services';
import {
  ActiveUnit,
  Booking,
  BookingDetails,
  Chassis,
  Container,
  Customer,
  EquipmentType,
  Genset,
  Redelivery,
  RedeliveryDetail,
  TransactionRow
} from 'types';
import { FilterOperator, FilterState, LogicalOperator } from 'utils';

import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field/text-field';
import { useSelectedLocation } from 'contexts';
import useConfirm from 'hooks/use-confirm';
import useTimeZone from 'hooks/use-timezone';
import useRedeliveryApi from 'pages/redeliveries/use-redelivery-api';
import unitDataColumns from '../../grid/unit-columns';
import '../bulk.scss';
import './account-transfer.scss';

interface TableCellProps<T extends BaseDataType, P extends string & keyof T> {
  column: ControlledDataTableColumn<T, P>;
  row: T;
  updateValue?: Customer;
}

const TransactionTableCell: FC<TableCellProps<TransactionRow, keyof TransactionRow>> = ({
  column,
  row,
  updateValue
}) => {
  const { rowSelection } = useDataTableContext<TransactionRow>();

  const valueLabel = column.getValueLabel ? column.getValueLabel(row[column.field], row) : row[column.field];

  const chassisSelected = !!row.chassis?.some(({ id }) => rowSelection.get(id));
  const containerSelected = !!row.container && rowSelection.get(row.container.id);
  const gensetSelected = !!row.gensets?.some(({ id }) => rowSelection.get(id));

  if (
    ((column.field === 'chassisCustomerName' && chassisSelected) ||
      (column.field === 'containerCustomerName' && containerSelected) ||
      (column.field === 'gensetCustomerName' && gensetSelected)) &&
    !!row.inbound &&
    !!updateValue
  ) {
    return (
      <div className="d-flex g-2 align-items-center updating">
        <span>{valueLabel}</span>
        <span className="material-icons">arrow_forward</span>
        <b>{updateValue?.name}</b>
      </div>
    );
  }

  if (
    (column.field === 'chassis' && chassisSelected) ||
    (column.field === 'container' && containerSelected) ||
    (column.field === 'gensets' && gensetSelected)
  ) {
    return <b>{valueLabel}</b>;
  }

  return <span>{valueLabel}</span>;
};

const UnitTableCell: FC<TableCellProps<ActiveUnit, keyof ActiveUnit>> = ({ column, row, updateValue }) => {
  const { newZonedDayJs } = useTimeZone();
  const { rowSelection } = useDataTableContext<ActiveUnit>();

  const valueLabel =
    column.field === 'receivedDate'
      ? newZonedDayJs(row[column.field]).format(column.dateFormat ?? 'M/D/YY LT')
      : column.getValueLabel
      ? column.getValueLabel(row[column.field], row)
      : row[column.field];

  if (column.field === 'customer' && rowSelection.get(row.id)) {
    return (
      <div className="d-flex g-2 align-items-center updating">
        <span>{valueLabel}</span>
        <span className="material-icons">arrow_forward</span>
        <b>{updateValue?.name}</b>
      </div>
    );
  }

  return <span>{valueLabel}</span>;
};

const AccountTransfer: FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const { dateTimeLocalToUtc, newZonedDayJs, utcToZonedDateTimeLocal } = useTimeZone();

  const { searchCustomers } = useCustomerAPI();
  const { transferUnit } = useUnitApi();
  const { findRedelivery } = useRedeliveryApi();

  const { getConfirmation, ConfirmationDialog } = useConfirm();

  const dataTableRef = useRef<DataTableRef>(null);

  const [data, setData] = useState<ActiveUnit[]>([]);
  const [rowSelection, setRowSelection] = useState<Map<number, boolean>>(new Map());

  const [loading, setLoading] = useState<boolean>(false);
  const [review, setReview] = useState<boolean>(false);
  const [transferDate, setTransferDate] = useState<string>();
  const [updateValue, setUpdateValue] = useState<Customer>();

  const [booking, setBooking] = useState<Booking>();
  const [bookingNumber, setBookingNumber] = useState<string>();
  const [chargeGateIn, setChargeGateIn] = useState<boolean>(true);
  const [chargeGateOut, setChargeGateOut] = useState<boolean>(true);
  const [ediGateIn, setEdiGateIn] = useState<boolean>(true);
  const [ediGateOut, setEdiGateOut] = useState<boolean>(true);
  const [redeliveryNumber, setRedeliveryNumber] = useState<string>();
  const [redelivery, setRedelivery] = useState<Redelivery>();

  const selectedIds: number[] = useMemo(() => {
    return [...rowSelection.entries()].filter(e => e[1]).map(e => e[0]);
  }, [rowSelection]);

  const mostRecentGateIn: dayjs.Dayjs = useMemo(() => {
    return data
      .filter(u => rowSelection.get(u.id))
      .map(u => dayjs(u.receivedDate))
      .reduce((mostRecent, day) => (day.isAfter(mostRecent) ? day : mostRecent), dayjs(0));
  }, [data, rowSelection]);

  const customerIds: Set<number> = useMemo(() => {
    return data
      .filter(u => rowSelection.get(u.id))
      .map(u => u.customer?.id)
      .reduce((acc, id) => (!id ? acc : acc.add(id)), new Set<number>());
  }, [data, rowSelection]);

  const sizeTypeCounts: Map<string, number> = useMemo(() => {
    return data
      .filter(u => rowSelection.get(u.id))
      .map(u => u.sizeType)
      .reduce((acc, sizeType) => {
        return !sizeType ? acc : acc.set(sizeType, (acc.get(sizeType) ?? 0) + 1);
      }, new Map<string, number>());
  }, [data, rowSelection]);

  const handleSubmit = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      setLoading(true);
      const skipped = new Map<number, boolean>();
      let ids = selectedIds.values();
      let it = ids.next();
      while (!it.done) {
        const id = it.value;
        let override = 1;
        if (booking?.id) {
          const unit = data.find(u => u.id === id);
          if (unit?.booking?.id && unit.booking.id !== booking.id) {
            override = await getConfirmation(
              'Override booking?',
              `Unit ${unit.unitNumber} currently reserved by ${unit.booking.bookingNumber}.`,
              'SKIP',
              'OVERRIDE'
            );
          }
        }
        if (override) {
          await transferUnit({
            id,
            booking,
            customer: updateValue,
            chargeGateIn,
            chargeGateOut,
            ediGateIn,
            ediGateOut,
            redeliveryId: redelivery?.id,
            transferDate
          });
        } else skipped.set(id, true);
        it = ids.next();
      }
      setRowSelection(skipped);
      setReview(false);
      setLoading(false);
    },
    [
      booking,
      data,
      chargeGateIn,
      chargeGateOut,
      ediGateIn,
      ediGateOut,
      getConfirmation,
      redelivery?.id,
      selectedIds,
      transferDate,
      updateValue,
      setLoading,
      setReview,
      transferUnit
    ]
  );

  const validateBookingNumber = useCallback(
    async (bookingNumber: string): Promise<ValidationResult> => {
      if (!bookingNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (customerIds.size !== 1) {
        return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
      } else if (!selectedLocation?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
      }
      const response = await getBookingByNumber(bookingNumber, customerIds.values().next().value, selectedLocation.id);
      if (!response) {
        return { status: ValidationStatus.WARNING, error: 'Booking not found' };
      }
      /* verify booking is active */
      const expiration: dayjs.Dayjs = dayjs.utc(response.expirationDate).local().endOf('day');
      if (expiration.isBefore(dayjs())) {
        return { status: ValidationStatus.WARNING, error: `Booking expired (${expiration.format('lll')})` };
      }
      const startDate: dayjs.Dayjs = dayjs.utc(response.startDate).local().startOf('day');
      if (startDate.isAfter(dayjs())) {
        return { status: ValidationStatus.WARNING, error: `Booking is not valid until ${startDate.format('lll')}` };
      }
      /* verify booking details */
      const result: ValidationResult = Object.entries(sizeTypeCounts).reduce(
        (acc: ValidationResult, [sizeType, count]) => {
          if (acc.status !== ValidationStatus.SUCCESS) {
            return acc;
          }
          /* verify container sizeType is present */
          const bookingDetails: BookingDetails[] =
            response.bookingDetails?.filter(d => d.iso?.includes(sizeType)) ?? [];
          if (!bookingDetails.length) {
            return { status: ValidationStatus.WARNING, error: `Booking does not include ${sizeType}` };
          }

          /* verify total quantity exceeds delivered count + reserved count (if unit not reserved) */
          const [delivered, quantity] = bookingDetails.reduce(
            (acc, d) => [acc[0] + (d.delivered ?? 0), acc[1] + (d.count ?? 0)],
            [0, 0]
          );
          if (quantity - delivered < count) {
            return {
              status: ValidationStatus.WARNING,
              error: `Booking quantity met (${quantity ?? 0} x ${sizeType})`
            };
          }
          return acc;
        },
        { status: ValidationStatus.SUCCESS }
      );
      if (result.status === ValidationStatus.SUCCESS) {
        setBooking(response);
      }
      return result;
    },
    [customerIds, selectedLocation?.id, sizeTypeCounts]
  );

    const validateRedelivery = useCallback(
      async (redeliveryNumber: string): Promise<ValidationResult> => {
        if (!redeliveryNumber) {
          return { status: ValidationStatus.DEFAULT };
        } else if (!updateValue) {
          return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
        } else if (!selectedLocation?.id) {
          return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
        }
        console.log(customerIds);
        const response = await findRedelivery(redeliveryNumber, updateValue.id, selectedLocation.id);
        if (!response) {
          return { status: ValidationStatus.WARNING, error: 'Redelivery not found' };
        }
        /* verify booking is active */
        const expiration: dayjs.Dayjs = dayjs.utc(response.expirationDate).local().endOf('day');
        if (expiration.isBefore(dayjs())) {
          return { status: ValidationStatus.WARNING, error: `Redelivery expired (${expiration.format('lll')})` };
        }
        const startDate: dayjs.Dayjs = dayjs.utc(response.startDate).local().startOf('day');
        if (startDate.isAfter(dayjs())) {
          return { status: ValidationStatus.WARNING, error: `Redelivery is not valid until ${startDate.format('lll')}` };
        }
        /* verify booking details */
        const result: ValidationResult = Object.entries(sizeTypeCounts).reduce(
          (acc: ValidationResult, [sizeType, count]) => {
            if (acc.status !== ValidationStatus.SUCCESS) {
              return acc;
            }
            /* verify container sizeType is present */
            const redeliveryDetails: RedeliveryDetail[] =
              response.redeliveryDetails?.filter(d => d.sizeType?.includes(sizeType)) ?? [];
            if (!redeliveryDetails.length) {
              return { status: ValidationStatus.WARNING, error: `Redelivery does not include ${sizeType}` };
            }
  
            /* verify total quantity exceeds delivered count + reserved count (if unit not reserved) */
            const [delivered, quantity] = redeliveryDetails.reduce(
              (acc, d) => [acc[0] + (d.received ?? 0), acc[1] + (d.count ?? 0)],
              [0, 0]
            );
            if (quantity - delivered < count) {
              return {
                status: ValidationStatus.WARNING,
                error: `Redelivery quantity met (${quantity ?? 0} x ${sizeType})`
              };
            }
            return acc;
          },
          { status: ValidationStatus.SUCCESS }
        );
        if (result.status === ValidationStatus.SUCCESS) {
          setRedelivery(response);
        }
        return result;
      },
      [customerIds, findRedelivery, selectedLocation?.id, sizeTypeCounts, updateValue]
    );

  const unitFetchCsv = useMemo(() => downloadUngroupedCsv({ active: true }), []);
  const unitFetchData = useMemo(() => fetchActiveUnitsUngrouped({ active: true }), []);

  const unitFilterState: FilterState = useMemo(
    () => ({
      logicalOperator: LogicalOperator.OR,
      filters: selectedIds.map(value => ({ field: 'id', filterOperator: FilterOperator.EQUALS, value }))
    }),
    [selectedIds]
  );

  const unitRenderCell = useCallback(
    (column: ControlledDataTableColumn<ActiveUnit>, row: ActiveUnit) => {
      return <UnitTableCell column={column} row={row} updateValue={updateValue} />;
    },
    [updateValue]
  );

  const transactionData: { [P: string]: Array<Partial<TransactionRow> & Pick<TransactionRow, 'id'>> } = useMemo(() => {
    const content = data
      .filter(u => rowSelection.get(u.id))
      .map((u, id) => ({
        id,
        completedDate: newZonedDayJs(transferDate || undefined).format('l LT'),
        ...(u.equipmentType === EquipmentType.CONTAINER
          ? {
              container: new Container(u),
              containerNumber: u.unitNumber,
              containerCustomerName: u.customer?.name
            }
          : u.equipmentType === EquipmentType.CHASSIS
          ? {
              chassis: [new Chassis(u)],
              chassisNumber: u.unitNumber,
              chassisCustomerName: u.customer?.name
            }
          : {
              gensets: [new Genset(u)],
              gensetNumber: u.unitNumber,
              gensetCustomerName: u.customer?.name
            })
      }));
    return {
      gateIn: content.map(row => ({ ...row, type: 'GATE IN', inbound: true })),
      gateOut: content.map(row => ({
        ...row,
        type: 'GATE OUT',
        inbound: false,
        completedDate: newZonedDayJs(transferDate || undefined)
          .subtract(1, 'minute')
          .format('l LT')
      }))
    };
  }, [data, rowSelection, transferDate, newZonedDayJs]);

  const transactionRenderCell = useCallback(
    (column: ControlledDataTableColumn<TransactionRow>, row: TransactionRow) => {
      return <TransactionTableCell column={column} row={row} updateValue={updateValue} />;
    },
    [updateValue]
  );

  if (review) {
    return (
      <div className="page" id="review-transfer">
        <ConfirmationDialog />
        <Loader loading={loading}>
          <div className="page-header">
            <h1>REVIEW TRANSFER</h1>
            <div className="grid-2">
              <button className="btn-outline" onClick={() => setReview(false)}>
                <span>CANCEL</span>
              </button>
              <button className="btn-primary" onClick={handleSubmit}>
                <span>SUBMIT</span>
              </button>
            </div>
          </div>
          <div className="page-content overflow-hidden d-flex flex-column g-3 h-100">
            <ControlledDataTableProvider<Partial<TransactionRow> & Pick<TransactionRow, 'id'>>
              initialColumns={[
                'type',
                'completedDate',
                'container',
                'containerCustomerName',
                'chassis',
                'chassisCustomerName',
                'gensets',
                'gensetCustomerName'
              ]}
              data={transactionData.gateOut}
              rowSelection={rowSelection}
            >
              <div className="card-primary h-50 mt-0">
                <div className="card-header">
                  <h4>Gate-out units under previous account</h4>
                  <div className="grid-3 place-items-center actions">
                    <TextField
                      id="gate-out-booking"
                      label="BOOKING"
                      value={bookingNumber ?? ''}
                      onChange={setBookingNumber}
                      validate={validateBookingNumber}
                    />
                    <div className="d-flex g-3 h-100 align-items-center">
                      <label htmlFor="edi-gate-out-checkbox">SEND GATE-OUT EDI</label>
                      <input
                        type="checkbox"
                        id="edi-gate-out-checkbox"
                        checked={ediGateOut}
                        onChange={e => setEdiGateOut(e.target.checked)}
                      />
                    </div>
                    <div className="d-flex g-3 h-100 align-items-center">
                      <label htmlFor="charge-gate-out-checkbox">CHARGE GATE-OUT</label>
                      <input
                        type="checkbox"
                        id="charge-gate-out-checkbox"
                        checked={chargeGateOut}
                        onChange={e => setChargeGateOut(e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ControlledDataTable<TransactionRow>
                    columns={transactionDataColumns}
                    fetchCsv={async () => {}}
                    fetchData={async () => ({})}
                    filterByLocation
                    renderCell={transactionRenderCell}
                  />
                </div>
              </div>
            </ControlledDataTableProvider>
            <ControlledDataTableProvider<Partial<TransactionRow> & Pick<TransactionRow, 'id'>>
              initialColumns={[
                'type',
                'completedDate',
                'container',
                'containerCustomerName',
                'chassis',
                'chassisCustomerName',
                'gensets',
                'gensetCustomerName'
              ]}
              data={transactionData.gateIn}
              rowSelection={rowSelection}
            >
              <div className="card-primary h-50 mt-0">
                <div className="card-header">
                  <h4>Gate-in units under {updateValue?.name ?? 'N/A'}</h4>
                  <div className="grid-3 place-items-center actions">
                    <TextField
                      id="gate-in-redelivery"
                      label="REDELIVERY"
                      value={redeliveryNumber ?? ''}
                      onChange={setRedeliveryNumber}
                      validate={validateRedelivery}
                    />
                    <div className="d-flex g-3 h-100 align-items-center">
                      <label htmlFor="edi-gate-in-checkbox">SEND GATE-IN EDI</label>
                      <input
                        type="checkbox"
                        id="edi-gate-in-checkbox"
                        checked={ediGateIn}
                        onChange={e => setEdiGateIn(e.target.checked)}
                      />
                    </div>
                    <div className="d-flex g-3 h-100 align-items-center">
                      <label htmlFor="charge-gate-in-checkbox">CHARGE GATE-IN</label>
                      <input
                        type="checkbox"
                        id="charge-gate-in-checkbox"
                        checked={chargeGateIn}
                        onChange={e => setChargeGateIn(e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ControlledDataTable<TransactionRow>
                    columns={transactionDataColumns}
                    fetchCsv={async () => {}}
                    fetchData={async () => ({})}
                    filterByLocation
                    renderCell={transactionRenderCell}
                  />
                </div>
              </div>
            </ControlledDataTableProvider>
          </div>
        </Loader>
      </div>
    );
  }

  return (
    <div className="page" id="account-transfer">
      <ErrorDialog />
      <SuccessDialog />
      <div className="page-header g-5">
        <h1>ACCOUNT TRANSFER</h1>
        <div className="grid-2">
          <button
            className="align-self-center btn-back d-flex g-2"
            onClick={() => navigate('/units', { replace: true })}
          >
            <span>BACK TO YARD MANAGEMENT</span>
            <span className="material-icons icon-large">grid_view</span>
          </button>
          <button className="btn-outline" onClick={() => navigate('/units/bulk', { replace: true })}>
            OTHER BULK UPDATES
          </button>
        </div>
      </div>
      <div className="page-content overflow-hidden">
        <ControlledDataTableProvider<ActiveUnit>
          initialColumns={['unitNumber', 'customer', 'sizeType', 'unitGrade', 'unitStatus', 'receivedDate', 'booking']}
          data={data}
          setData={setData}
          filterState={unitFilterState}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        >
          <ControlledDataTable<ActiveUnit>
            checkboxSelection
            columns={unitDataColumns}
            dataTableRef={dataTableRef}
            fetchCsv={unitFetchCsv}
            fetchData={unitFetchData}
            filterByLocation
            renderCell={unitRenderCell}
            searchLogicalOperator={LogicalOperator.OR}
            tableToolbar
          >
            <div className="flex-grow-1 d-flex justify-content-end">
              <form
                className="flex-grow-1 grid-4"
                onSubmit={e => {
                  e.preventDefault();
                  setReview(true);
                }}
              >
                <div className="col-start-2 h-100">
                  <AsyncSearch<Customer>
                    required
                    label="ACCOUNT"
                    formatOption={({ name }) => name}
                    formatValue={customer => customer?.name ?? ''}
                    handleSelect={setUpdateValue}
                    search={searchCustomers}
                    value={updateValue}
                  />
                </div>
                <div className="text-field d-flex">
                  <input
                    id="transfer-date-input"
                    type="datetime-local"
                    className="h-100 w-100"
                    disabled={!transferDate}
                    max={utcToZonedDateTimeLocal(newZonedDayJs())}
                    min={utcToZonedDateTimeLocal(newZonedDayJs(mostRecentGateIn).add(1, 'minute'))}
                    onChange={e => setTransferDate(dateTimeLocalToUtc(e.target.value))}
                    value={utcToZonedDateTimeLocal(transferDate)}
                  />
                  <label htmlFor="transfer-date-input">TRANSFER DATE</label>
                  <div id="lock-transfer-date" className="material-icons icon-large">
                    <input
                      type="checkbox"
                      id="backdate-checkbox"
                      checked={!!transferDate}
                      onChange={e => setTransferDate(!e.target.checked ? undefined : newZonedDayJs().toISOString())}
                    />
                  </div>
                </div>
                <button
                  className="btn-primary h-100"
                  disabled={!rowSelection.size}
                  key={rowSelection.size}
                  type="submit"
                >
                  REVIEW ({rowSelection.size})
                </button>
              </form>
            </div>
          </ControlledDataTable>
        </ControlledDataTableProvider>
      </div>
    </div>
  );
};

export default AccountTransfer;
